import { forwardRef } from 'react'
import clsx from 'clsx'
import { Loader2 } from 'lucide-react'

/**
 * Button.
 *
 * One primary action per screen — the `primary` variant is filled and should
 * appear once. Everything else is `secondary` (outlined) or `ghost`.
 *
 * The loading state keeps the button's width by rendering the label alongside
 * the spinner rather than replacing it. Swapping the label for a spinner makes
 * the button shrink mid-click, which moves whatever is next to it.
 */

const VARIANTS = {
  primary:
    'bg-[var(--color-accent)] text-white border border-transparent ' +
    'hover:bg-[var(--color-accent-text)] active:bg-[var(--color-accent-hover)]',

  secondary:
    'bg-[var(--color-surface)] text-[var(--color-ink-900)] border border-[var(--color-line-strong)] ' +
    'hover:bg-[var(--color-surface-sunken)] hover:border-[var(--color-ink-400)]',

  ghost:
    'bg-transparent text-[var(--color-ink-700)] border border-transparent ' +
    'hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink-900)]',

  // Destructive actions are visually separated from the primary path, per the
  // interaction guidance: red, and never the default focus of a screen.
  danger:
    'bg-[var(--color-danger)] text-white border border-transparent ' +
    'hover:brightness-110 active:brightness-95',

  dangerOutline:
    'bg-transparent text-[var(--color-danger)] border border-[var(--color-danger)] ' +
    'hover:bg-[var(--color-danger-bg)]',
}

const SIZES = {
  // 44px minimum on the default size — the touch-target floor, and this is
  // used on phones far more than on desks.
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-4 text-[0.9375rem] gap-2',
  lg: 'h-12 px-6 text-base gap-2',
  icon: 'h-11 w-11 p-0',
}

const Button = forwardRef(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    disabled = false,
    icon: Icon,
    iconRight: IconRight,
    className,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      // Announced to assistive tech, not just styled.
      aria-busy={loading || undefined}
      className={clsx(
        'inline-flex items-center justify-center rounded-md font-medium',
        'transition-colors duration-150',
        'cursor-pointer select-none whitespace-nowrap',
        // Disabled must look unavailable AND not accept the cursor, or people
        // click it repeatedly and assume the app is broken.
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
        VARIANTS[variant] ?? VARIANTS.secondary,
        SIZES[size] ?? SIZES.md,
        className,
      )}
      {...props}
    >
      {loading ? (
        <Loader2 size={16} className="animate-spin shrink-0" aria-hidden="true" />
      ) : (
        Icon && <Icon size={16} className="shrink-0" aria-hidden="true" />
      )}

      {children}

      {IconRight && !loading && <IconRight size={16} className="shrink-0" aria-hidden="true" />}
    </button>
  )
})

export default Button

/**
 * Icon-only button.
 *
 * Requires a `label`, which becomes the accessible name and the tooltip. An
 * icon-only control with no label is unusable with a screen reader and
 * ambiguous with one, so the prop is not optional.
 */
export function IconButton({ label, icon: Icon, className, ...props }) {
  if (import.meta.env.DEV && !label) {
    console.warn('[IconButton] a `label` is required for accessibility.')
  }

  return (
    <Button
      size="icon"
      variant="ghost"
      aria-label={label}
      title={label}
      className={className}
      {...props}
    >
      {Icon && <Icon size={18} aria-hidden="true" />}
    </Button>
  )
}
